import React, { useMemo } from 'react';
import { Badge, Section, labelize, text } from './PlaceWorkbenchParts';

const COMPOSER_STRUCTURED_FIELDS = [
  { field: 'retrieval_lane', label: 'Retrieval lane', required: true },
  { field: 'category', label: 'Card category', required: true },
  { field: 'budget', label: 'Budget' },
  { field: 'seasonality', label: 'Seasonality' },
  { field: 'booking_logic', label: 'Booking logic' },
  { field: 'weather_logic', label: 'Weather logic' },
  { field: 'parking_access', label: 'Arrival & parking' },
];

const PRACTICAL_COMPOSER_STRUCTURED_FIELDS = [
  { field: 'utility_kind', label: 'Utility kind' },
];

const CORE_MATCH_DIMENSIONS = [
  'match_trip_intent',
  'match_party',
  'match_daypart',
  'match_duration',
  'match_physical_effort',
  'match_accessibility',
];

function signalIdentity(signal) {
  return `${signal?.dimension || signal?.signal_family || ''}:${signal?.value || signal?.signal || ''}`;
}

function groupedSignals(signals) {
  return (signals || []).reduce((acc, signal) => {
    const dimension = signal.dimension || signal.signal_family || 'unknown';
    if (!acc[dimension]) acc[dimension] = [];
    acc[dimension].push(signal);
    return acc;
  }, {});
}

function composerStructuredFields(cardForm) {
  const fields = [...COMPOSER_STRUCTURED_FIELDS];
  if (cardForm.retrieval_lane === 'practical_find') fields.push(...PRACTICAL_COMPOSER_STRUCTURED_FIELDS);
  return fields;
}

function hasComposerValue(cardForm, field) {
  const value = cardForm[field];
  return Array.isArray(value) ? value.length > 0 : Boolean(text(value));
}

function composerFieldValue(cardForm, field) {
  const value = cardForm[field];
  if (Array.isArray(value)) return value.length > 0 ? value.map(labelize).join(', ') : '-';
  return text(value) ? labelize(value) : '-';
}

function missingMatchDimensions(approvedMatchSignals, pendingMatchSignals) {
  const covered = new Set([
    ...(approvedMatchSignals || []).map(signal => signal.dimension).filter(Boolean),
    ...(pendingMatchSignals || []).map(signal => signal.dimension || signal.signal_family).filter(Boolean),
  ]);
  return CORE_MATCH_DIMENSIONS.filter(dimension => !covered.has(dimension));
}

function acceptedUnknownDimensions(acceptedUnknownMatchSignals) {
  return new Set((acceptedUnknownMatchSignals || []).map(row => row.dimension).filter(Boolean));
}

export function composerSignalIssueCountFor(cardForm, approvedMatchSignals, pendingMatchSignals, acceptedUnknownMatchSignals = []) {
  const fields = composerStructuredFields(cardForm);
  const missingFields = fields.filter(field => !hasComposerValue(cardForm, field.field));
  const acceptedUnknown = acceptedUnknownDimensions(acceptedUnknownMatchSignals);
  const unresolvedDimensions = missingMatchDimensions(approvedMatchSignals, pendingMatchSignals).filter(
    dimension => !acceptedUnknown.has(dimension)
  );
  return missingFields.length + unresolvedDimensions.length + (pendingMatchSignals || []).length;
}

export default function ComposerSignalsTab({
  cardForm,
  detail,
  approvedMatchSignals,
  pendingMatchSignals,
  acceptedUnknownMatchSignals = [],
  onEditContent,
  onReviewChanges,
  onEnrichCard,
  onAcceptUnknown,
  acceptingUnknownDimension,
  canRunEnrichment,
}) {
  const structuredFields = useMemo(() => composerStructuredFields(cardForm), [cardForm]);
  const missingStructuredFields = useMemo(
    () => structuredFields.filter(field => !hasComposerValue(cardForm, field.field)),
    [cardForm, structuredFields]
  );
  const acceptedUnknown = useMemo(
    () => acceptedUnknownDimensions(acceptedUnknownMatchSignals),
    [acceptedUnknownMatchSignals]
  );
  const missingDimensions = useMemo(
    () => missingMatchDimensions(approvedMatchSignals, pendingMatchSignals).filter(dimension => !acceptedUnknown.has(dimension)),
    [approvedMatchSignals, pendingMatchSignals, acceptedUnknown]
  );
  const approvedGroups = groupedSignals(approvedMatchSignals);
  const pendingGroups = groupedSignals(pendingMatchSignals);
  const approvedDimensions = Object.keys(approvedGroups).sort();
  const pendingDimensions = Object.keys(pendingGroups).sort();
  const useCaseTags = Array.isArray(cardForm.guest_use_case_tags) ? cardForm.guest_use_case_tags : [];
  const coverageTiles = [
    ['Use-case tags', useCaseTags.length],
    ['Structured fields', structuredFields.length - missingStructuredFields.length],
    ['Match signals', (approvedMatchSignals || []).length],
    ['Accepted unknown', acceptedUnknownMatchSignals.length],
    ['Pending signals', (pendingMatchSignals || []).length],
  ];

  return (
    <div className="grid gap-4">
      <Section eyebrow="Composer read path" title="Signal Coverage">
        <div className="grid gap-3 md:grid-cols-5">
          {coverageTiles.map(([label, value]) => (
            <div key={label} className="rounded-lg border border-stone-200 bg-white p-3">
              <p className="text-xs font-medium text-stone-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold text-stone-900">{value}</p>
            </div>
          ))}
        </div>
        {(missingStructuredFields.length > 0 || missingDimensions.length > 0) && (
          <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-yellow-900">Missing Composer coverage</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {missingStructuredFields.map(field => <Badge key={`field-${field.field}`} kind="warning">{field.label}</Badge>)}
                  {missingDimensions.map(dimension => (
                    <span key={`match-${dimension}`} className="inline-flex items-center gap-1.5 rounded-full border border-yellow-300 bg-white px-2 py-1 text-xs text-yellow-900">
                      {labelize(dimension)}
                      {onAcceptUnknown && (
                        <button
                          type="button"
                          onClick={() => onAcceptUnknown(dimension)}
                          disabled={acceptingUnknownDimension === dimension}
                          className="font-medium text-yellow-950 underline underline-offset-2 disabled:opacity-50"
                        >
                          {acceptingUnknownDimension === dimension ? 'Saving...' : 'Accept unknown'}
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={onEditContent} className="rounded-lg border border-yellow-300 bg-white px-3 py-2 text-sm font-medium text-yellow-900 hover:bg-yellow-50">
                  Edit fields
                </button>
                <button type="button" onClick={onEnrichCard} disabled={!canRunEnrichment} className="rounded-lg bg-yellow-900 px-3 py-2 text-sm font-medium text-white hover:bg-yellow-800 disabled:opacity-50">
                  Enrich card
                </button>
              </div>
            </div>
          </div>
        )}
        {acceptedUnknownMatchSignals.length > 0 && (
          <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3">
            <p className="text-sm font-medium text-stone-800">Accepted unknown coverage</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {acceptedUnknownMatchSignals.map(row => <Badge key={row.dimension}>{labelize(row.dimension)}</Badge>)}
            </div>
          </div>
        )}
      </Section>

      <Section eyebrow="Governed taxonomy" title="Structured Fields">
        <div className="grid gap-2">
          {structuredFields.map(field => {
            const filled = hasComposerValue(cardForm, field.field);
            return (
              <div key={field.field} className="grid gap-2 rounded-lg border border-stone-200 bg-white p-3 text-sm md:grid-cols-[180px_1fr_auto] md:items-center">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-stone-700">{field.label}</span>
                  {field.required && <Badge kind="accent">Required</Badge>}
                </div>
                <p className={filled ? 'text-stone-900' : 'text-stone-400'}>{composerFieldValue(cardForm, field.field)}</p>
                <Badge kind={filled ? 'success' : 'warning'}>{filled ? 'Set' : 'Missing'}</Badge>
              </div>
            );
          })}
        </div>
      </Section>

      <Section eyebrow="Guest intent" title="Use-Case Tags">
        {useCaseTags.length === 0 ? (
          <p className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">No use-case tags selected.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {useCaseTags.map(tag => <Badge key={tag} kind="success">{labelize(tag)}</Badge>)}
          </div>
        )}
      </Section>

      <Section eyebrow="Governed signals" title="Approved Match Signals">
        {approvedDimensions.length === 0 ? (
          <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-500">No approved match signals yet.</p>
        ) : (
          <div className="grid gap-3">
            {approvedDimensions.map(dimension => (
              <div key={dimension} className="rounded-lg border border-stone-200 bg-white p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium text-stone-900">{labelize(dimension)}</p>
                  {approvedGroups[dimension].some(signal => signal.hard_filter) && <Badge kind="danger">Hard filter</Badge>}
                </div>
                <div className="grid gap-2">
                  {approvedGroups[dimension].map(signal => (
                    <div key={signal.id || signalIdentity(signal)} className="rounded-md border border-blue-100 bg-blue-50/40 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-stone-900">{signal.label || labelize(signal.value)}</span>
                        {signal.composer_role && <Badge>{labelize(signal.composer_role)}</Badge>}
                        {signal.basis && <span className="text-xs text-stone-500">{labelize(signal.basis)}</span>}
                        {signal.confidence && <span className="text-xs text-stone-500">{labelize(signal.confidence)} confidence</span>}
                      </div>
                      {signal.evidence_snippet && <p className="mt-1 text-xs leading-5 text-stone-600">{signal.evidence_snippet}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section eyebrow="Review queue" title="Pending Match Signals">
        {pendingDimensions.length === 0 ? (
          <p className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-500">No pending match-signal proposals.</p>
        ) : (
          <div className="grid gap-3">
            {pendingDimensions.map(dimension => (
              <div key={dimension} className="rounded-lg border border-blue-100 bg-white p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-stone-900">{labelize(dimension)}</p>
                  <button type="button" onClick={onReviewChanges} className="text-xs font-medium text-blue-800 underline underline-offset-2">Review</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {pendingGroups[dimension].map(signal => (
                    <Badge key={signal.id || signalIdentity(signal)} kind="accent">{signal.match_label || signal.display_text || labelize(signal.value || signal.signal)}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {detail?.published_item && (
        <Section eyebrow="Published projection" title="Composer Snapshot">
          <p className="text-sm text-stone-600">This card has a published retrieval item. The Composer should read from the published projection, not working-card drafts.</p>
        </Section>
      )}
    </div>
  );
}
